import { AccountingDocumentsService } from '@/services/accounting-documents.service';
import { AccountBalanceDto } from '@onrunning/backstage-models';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const accountingDocumentsService = new AccountingDocumentsService();

export const useAccountBalanceStore = defineStore('account-balance', () => {
  const accountBalance = ref<AccountBalanceDto | undefined>(undefined);
  const cachedAccountId = ref<string | undefined>(undefined);
  const loadingAccountBalance = ref<boolean>(false);

  const creditCardAnnualCap = computed(() => {
    return accountBalance.value?.creditCardPaymentInformation?.restrictions.annualCap ?? 0;
  });

  const isCreditCardPaymentEnabled = computed<boolean>(() => {
    const restrictions = accountBalance.value?.creditCardPaymentInformation?.restrictions;

    return !restrictions?.isFullyRestricted;
  });

  const isCreditCardPaymentExceeded = computed(() => {
    return (documentsTotal: number) => {
      if (accountBalance.value?.creditCardPaymentInformation) {
        const { ytdPaymentsAmount = 0 } = accountBalance.value.creditCardPaymentInformation;
        const { annualCap, annualCapBuffer } = accountBalance.value.creditCardPaymentInformation.restrictions;

        // if the used amount is already over the annual cap, we block any further payments
        if (ytdPaymentsAmount > annualCap) {
          return true;
        }

        return documentsTotal + ytdPaymentsAmount > annualCap + annualCapBuffer;
      }

      return false;
    };
  });

  // actions
  const getAccountBalance = async (accountIdParam: string) => {
    if (cachedAccountId.value === accountIdParam && accountBalance.value) {
      return;
    }

    loadingAccountBalance.value = true;

    const validations = await accountingDocumentsService.getBalance(accountIdParam);

    if (validations.model) {
      accountBalance.value = validations.model;
      cachedAccountId.value = accountIdParam;
    }

    loadingAccountBalance.value = false;
  };

  const $reset = () => {
    accountBalance.value = undefined;
    cachedAccountId.value = undefined;
    loadingAccountBalance.value = false;
  };

  return {
    accountBalance,
    loadingAccountBalance,
    isCreditCardPaymentExceeded,
    isCreditCardPaymentEnabled,
    getAccountBalance,
    creditCardAnnualCap,
    $reset,
  };
});
